// Encabezado de la ficha del SKU sobre el `DetailHeader` de Cartera: nombre, chips, SKU copiable,
// cuatro datos y las alertas cortas.
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import { cn } from '@/lib/cn';
import { formatMxn, mxn } from '@/lib/dineroHelpers';
import { Skeleton } from '@/kit';
import { formatInt, NOT_APPLICABLE, TIER_OPTIONS } from '../../lib/inventoryFormat';
import { alertDetails, coverageDaysText, salesMetricLabel } from '../../lib/inventoryDetail';
import { tracksStock, type InventoryRow } from '../../lib/inventoryList';
import { KindChip } from '@/shared/patterns/KindChip';
import { StockAlertChip } from '@/shared/patterns/StockAlertChip';
import { CreateSalePromotionLink } from '../LaCentralAction';
import { DetailHeader, type DetailHeaderFact } from '@/shared/patterns/DetailHeader';

/** Precio vigente y de dónde salió; `source` es `null` mientras no se lee el historial. */
export interface HeaderPrice {
    cents: number | null;
    source: string | null;
}

const CHIP =
    'inline-flex items-center whitespace-nowrap rounded-full border px-1.5 py-px text-[9.5px] font-semibold';

export function SkuHeader({
    row,
    name,
    period,
    storeCode,
    price,
    onPriceClick,
}: {
    row: InventoryRow;
    /** Nombre visible, con la descripción del catálogo si la hay; `null` mientras carga. */
    name: string | null;
    period: InventoryLensPeriod;
    /** Código de la tienda filtrada; ausente en consolidado. */
    storeCode: string | undefined;
    price: HeaderPrice;
    onPriceClick: (() => void) | null;
}) {
    const storesTotal = row.byStore.length;
    const notApplicable = row.kind === 'CARD';
    const priceText = notApplicable
        ? NOT_APPLICABLE
        : price.cents === null
          ? '—'
          : formatMxn(mxn(price.cents));
    const tier = TIER_OPTIONS.find((o) => o.value === row.tier);
    const alerts = alertDetails(row);
    const salesLabel = salesMetricLabel(period);
    const facts: DetailHeaderFact[] = [
        tracksStock(row)
            ? {
                  key: 'stock',
                  label: 'Stock',
                  value: formatInt(row.stock),
                  sub: `en ${String(row.storesWithStock)} de ${String(storesTotal)} tienda${storesTotal === 1 ? '' : 's'}${storeCode ? ` · filtrado a ${storeCode}` : ''}`,
              }
            : { key: 'stock', label: 'Stock', value: 'Sin inventario', wrap: true },
        {
            key: 'price',
            label: 'Precio',
            value: onPriceClick ? (
                <button
                    type="button"
                    title="Ver historial de precios"
                    onClick={onPriceClick}
                    className="text-left underline-offset-2 hover:underline"
                >
                    {priceText}
                </button>
            ) : (
                priceText
            ),
            sub: price.source === null ? undefined : `Origen: ${price.source}`,
        },
        {
            key: 'sales',
            label: salesLabel.label,
            title: salesLabel.title,
            value: notApplicable ? NOT_APPLICABLE : row.sales === null ? '—' : formatInt(row.sales),
        },
        {
            key: 'coverage',
            label: 'Cobertura',
            value: notApplicable ? NOT_APPLICABLE : coverageDaysText(row.coverage, row.stock),
            wrap: true,
        },
    ];
    return (
        <section aria-label="Resumen del SKU" className="flex-none">
            <DetailHeader
                name={name ?? <Skeleton className="h-4 w-32" />}
                busy={name === null}
                badges={
                    <>
                        <KindChip kind={row.kind} />
                        {row.tier === 'NONE' || tier === undefined ? null : (
                            <span
                                className={cn(CHIP, 'border-border bg-muted text-muted-foreground')}
                            >
                                {`Gama ${tier.label}`}
                            </span>
                        )}
                    </>
                }
                idLabel="SKU"
                idValue={row.sku}
                idAction={<CreateSalePromotionLink sku={row.sku} />}
                note={
                    alerts.length === 0 ? undefined : (
                        <ul aria-label="Alertas" className="flex flex-wrap gap-1">
                            {alerts.map((alert) => (
                                <StockAlertChip
                                    key={alert.key}
                                    as="li"
                                    alert={alert.key}
                                    title={alert.text}
                                >
                                    {alert.label}
                                </StockAlertChip>
                            ))}
                        </ul>
                    )
                }
                facts={facts}
            />
        </section>
    );
}
